import { useState, useLayoutEffect } from 'react'
import {
  useFloating,
  flip,
  shift,
  offset,
  size,
  Placement,
  useDismiss,
  useInteractions,
} from '@floating-ui/react'
import type { Strategy } from '@floating-ui/react'
import { autoUpdate } from '@floating-ui/dom'

type PoppableOptions = { offset: number }

type PoppableProps = {
  placement?: Placement
  options?: PoppableOptions
  computeSize?: boolean
  isVisible?: boolean
  isDismissable?: boolean
  virtualReference?: {
    getBoundingClientRect: () => DOMRect
  } | null
  onDismiss?: () => void
}

export type PoppableStyle = {
  position: Strategy
  top: string | number
  left: string | number
}

export function usePoppable({
  placement = 'bottom-start',
  options = { offset: 4 },
  computeSize,
  virtualReference,
  isVisible = false,
  isDismissable = true,
  onDismiss,
}: PoppableProps = {}) {
  const [isPoppableVisible, setIsPoppableVisible] = useState(isVisible)

  function onDismissPoppable(isVisible: boolean) {
    setIsPoppableVisible(isVisible)
    onDismiss?.()
  }

  const { x, y, strategy, refs, context } = useFloating({
    open: isPoppableVisible,
    onOpenChange: onDismissPoppable,
    placement,
    middleware: [
      offset(options?.offset),
      shift(),
      flip(),
      ...(computeSize
        ? [
            size({
              apply({ availableWidth, availableHeight }) {
                Object.assign(refs.floating.current!.style, {
                  maxWidth: `${availableWidth}px`,
                  height: `${availableHeight - 8}px`,
                })
              },
            }),
          ]
        : []),
    ],
    whileElementsMounted: autoUpdate,
  })

  const dismiss = useDismiss(context, { enabled: isDismissable })
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  useLayoutEffect(() => {
    if (!!virtualReference) {
      refs.setPositionReference({
        getBoundingClientRect: virtualReference.getBoundingClientRect,
      })
    }
  }, [refs.setPositionReference, virtualReference])

  return {
    style: {
      position: strategy,
      top: y ?? '',
      left: x ?? '',
    },
    isPoppableVisible,
    setIsPoppableVisible,
    refs,
    context,
    getReferenceProps,
    getFloatingProps,
  }
}
